import React from 'react';
import Moment from 'moment';
import { TimeRange, TimeSeries } from 'pondjs';
import { Resizable, ChartContainer, ChartRow, Charts, YAxis, LineChart, Legend, styler, EventMarker } from 'react-timeseries-charts';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Typography from '@material-ui/core/Typography';
import CardContent from '@material-ui/core/CardContent';

const colors = [
	'red',
	'green',
	'orange',
	'blue',
	'purple',
	'#556B2F'
];

export default class InstallationsReport extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			application: '3ftlLhJYXpVqmP5joDcbVW',
			timeRange: null,
			maxTimeRange: null,
			series: null,
			columnsState: null,
			fetchingInstallations: false,
			pastWeekInstallations: null,
			pastMonthInstallations: null,
			totalInstallations: null
		};

		this.fetchInstallations = this.fetchInstallations.bind(this);
		this.handleSelectApplication = this.handleSelectApplication.bind(this);
		this.handleTrackerChanged = this.handleTrackerChanged.bind(this);
		this.handleTimeRangeChange = this.handleTimeRangeChange.bind(this);
		this.handleColumnActiveChange = this.handleColumnActiveChange.bind(this);
	}

	componentDidMount() {

		this.fetchInstallations(this.state.application);
	}

	fetchInstallations(applicationId) {

		this.setState({ fetchingInstallations: true });

		this.props.fetchApi('get', `/installationsData?applicationId=${applicationId}`)
			.then(response => {

				let { timeRange, series, columnsState, pastWeekInstallations, pastMonthInstallations, totalInstallations } = this.processInstallations(response.data.data);

				this.setState({
					maxTimeRange: timeRange,
					timeRange,
					series,
					columnsState,
					fetchingInstallations: false,
					pastWeekInstallations,
					pastMonthInstallations,
					totalInstallations
				});
			}).catch(error => {

				console.error(error);
				this.setState({ fetchingInstallations: false });
			});
	}

	processInstallations(installationsData) {

		if (!installationsData || Object.keys(installationsData).length === 0) {
			return {
				timeRange: null,
				series: [],
				columnsState: {},
				pastWeekInstallations: {},
				pastMonthInstallations: {},
				totalInstallations: {}
			};
		}

		let minDate;
		let maxDate;

		const today = Moment().startOf('day');

		const pastWeekInstallations = {};
		const pastMonthInstallations = {};
		const totalInstallations = {};

		const totalInstallationsData = {};

		const platformsSeries = Object.keys(installationsData).map(platformName => {

			let maxInstallationCount = 0;

			const platformDataPoints = Object.keys(installationsData[platformName]).map(dateString => {

				const installationCount = installationsData[platformName][dateString];
				const currentDate = Moment(dateString).startOf('day');

				if (!totalInstallations.hasOwnProperty(platformName))
					totalInstallations[platformName] = 0;
				totalInstallations[platformName] += installationCount;

				if (today.diff(currentDate, 'days') <= 7) {
					
					if (!pastWeekInstallations.hasOwnProperty(platformName))
						pastWeekInstallations[platformName] = 0;
					pastWeekInstallations[platformName] += installationCount;
				}

				if (today.diff(currentDate, 'days') <= 30) {
					
					if (!pastMonthInstallations.hasOwnProperty(platformName))
						pastMonthInstallations[platformName] = 0;
					pastMonthInstallations[platformName] += installationCount;
				}

				if (!minDate || currentDate < minDate)
					minDate = currentDate;
				else if (!maxDate || currentDate > maxDate)
					maxDate = currentDate;

				if (!totalInstallationsData.hasOwnProperty(dateString))
					totalInstallationsData[dateString] = 0;

				totalInstallationsData[dateString] += installationCount;

				maxInstallationCount = Math.max(installationCount, maxInstallationCount);

				return [currentDate, installationCount];
			});

			const seriesData = {
				name: platformName,
				columns: ['time', platformName],
				points: platformDataPoints
			};

			return {
				name: platformName,
				label: platformName,
				maxInstallationsCount: maxInstallationCount,
				data: new TimeSeries(seriesData)
			};
		});

		let maxInstallationTotalCount = 0;

		const totalInstallationPoints = Object.keys(totalInstallationsData).sort().map(dateString => {

			const installationCount = totalInstallationsData[dateString];
			const currentDate = Moment(dateString).startOf('day');

			maxInstallationTotalCount = Math.max(installationCount, maxInstallationTotalCount);

			return [currentDate, installationCount];
		});

		const totalInstallationsSeriesData = {
			name: 'total',
			columns: ['time', 'total'],
			points: totalInstallationPoints
		};

		let timeRange = new TimeRange(minDate || today, maxDate.add(3, 'days') || today);

		const totalSeries = {
			name: 'total',
			label: 'Total',
			maxInstallationsCount: maxInstallationTotalCount,
			data: new TimeSeries(totalInstallationsSeriesData)
		};
		const series = [totalSeries, ...platformsSeries];

		const columnsState = series.reduce((aggregator, serie) => {
			aggregator[serie.name] = true;
			return aggregator;
		}, {});

		return {
			timeRange,
			series,
			columnsState,
			pastWeekInstallations,
			pastMonthInstallations,
			totalInstallations
		};
	}

	handleTrackerChanged(t) {
		const { series } = this.state;

		if (series && t) {
			const trackerEvents = series.reduce((aggregator, serie) => {

				const event = serie.data.atTime(t);
				aggregator[serie.name] = {
					event,
					value: event.get(serie.name)
				};

				return aggregator;
			}, {});

			this.setState({ trackerEvents });
		}
	}

	handleTimeRangeChange(timeRange) {
		if (!timeRange)
			timeRange = this.state.maxTimeRange;

		this.setState({ timeRange: timeRange, brushrange: timeRange });
	}

	handleColumnActiveChange(key) {

		this.setState((prevState) => (
			{
				columnsState: {
					...prevState.columnsState,
					[key]: !prevState.columnsState[key]
				}
			}
		));
	}

	handleSelectApplication(event, index) {
		this.setState(() => ({ application: event.target.value }));
		this.fetchInstallations(event.target.value);
	}

	render() {

		const {
			application,
			fetchingInstallations,
			series,
			columnsState,
			pastWeekInstallations,
			pastMonthInstallations,
			totalInstallations,
			trackerEvents
		} = this.state;

		let content = null;

		if (fetchingInstallations) {
			content = (
				<div style={{textAlign: 'center', padding: '4em'}}>
					<CircularProgress size={60} />
				</div>
			);
		} else if (series === null) {
			content = null;
		} else if (series.length === 0) {
			content = (
				<p>Não há instalações a serem exibidas</p>
			);
		} else {
			const style = styler(series.map((serie, index) => ({ key: serie.name, color: colors[index] })));

			const legend = series.map((serie, index) => (
				{
					key: serie.name,
					label: serie.label,
					disabled: !columnsState[serie.name]
				}
			));

			const maxInstallationsCount =
				series
				.filter(serie => columnsState[serie.name])
				.reduce((currentMax, serie) => Math.max(currentMax, serie.maxInstallationsCount), 0);

			content = (
				<div style={{padding: '0 3em 3em 3em'}}>
					<Legend type="dot" style={style} categories={legend} onSelectionChange={this.handleColumnActiveChange} />
					<Resizable>
						<ChartContainer
							timeRange={this.state.timeRange}
							width={800}
							onTrackerChanged={this.handleTrackerChanged}
							onTimeRangeChanged={this.handleTimeRangeChange}
							minTime={this.state.maxTimeRange.begin()}
							maxTime={this.state.maxTimeRange.end()}
							enablePanZoom={true}>
							<ChartRow height="200">
								<YAxis id="installationsAxis" label="Instalações" min={0} max={maxInstallationsCount} width="60" type="linear" format="d"/>
								<Charts>
									{
										series
										&&
										series.length > 0
										&&
										series.filter(serie => columnsState[serie.name]).map((serie, index) => (
											<LineChart
												key={index}
												columns={[serie.name]}
												axis="installationsAxis"
												series={serie.data}
												style={style} />
										))
									}
									{
										series
										&&
										series.length > 0
										&&
										series.filter(serie => {
											return columnsState[serie.name] === true && trackerEvents && trackerEvents.hasOwnProperty(serie.name);
										}).map((serie, index) => {
											return <EventMarker
													key={serie.name}
													type="flag"
													axis="installationsAxis"
													event={trackerEvents[serie.name].event}
													column={serie.name}
													info={trackerEvents[serie.name].value + ''}
													infoTimeFormat="%d/%m/%Y"
													markerRadius={2}
													markerStyle={{ fill: "#2db3d1" }}
												/>;
										})
									}
								</Charts>
							</ChartRow>
						</ChartContainer>
					</Resizable>
					<Card style={{ marginTop: '2em' }}>
						<CardHeader
							title='Instalações nos últimos 7 dias'
						/>
						<CardContent>
							{
								Object.keys(pastWeekInstallations).map(platformName =>
									<div key={platformName} style={{marginBottom: '1em'}}><span style={{fontWeight: 'bold'}}>{platformName}:</span> <span>{ pastWeekInstallations[platformName] }</span></div>
								)
							}
							{
								<div style={{marginBottom: '1em'}}><span style={{fontWeight: 'bold'}}>Total:</span> <span>{ Object.keys(pastWeekInstallations).reduce((total, platformName) => total + pastWeekInstallations[platformName], 0) }</span></div>
							}
						</CardContent>
					</Card>
					<Card style={{ marginTop: '2em' }}>
						<CardHeader
							title='Instalações nos últimos 30 dias'
						/>
						<CardContent>
							{
								Object.keys(pastMonthInstallations).map(platformName =>
									<div key={platformName} style={{marginBottom: '1em'}}><span style={{fontWeight: 'bold'}}>{platformName}:</span> <span>{ pastMonthInstallations[platformName] }</span></div>
								)
							}
							{
								<div style={{marginBottom: '1em'}}><span style={{fontWeight: 'bold'}}>Total:</span> <span>{ Object.keys(pastMonthInstallations).reduce((total, platformName) => total + pastMonthInstallations[platformName], 0) }</span></div>
							}
						</CardContent>
					</Card>
					<Card style={{ marginTop: '2em' }}>
						<CardHeader
							title='Número total de instalações'
						/>
						<CardContent>
							{
								Object.keys(totalInstallations).map(platformName =>
									<div key={platformName} style={{marginBottom: '1em'}}><span style={{fontWeight: 'bold'}}>{platformName}:</span> <span>{ totalInstallations[platformName] }</span></div>
								)
							}
							{
								<div style={{marginBottom: '1em'}}><span style={{fontWeight: 'bold'}}>Total:</span> <span>{ Object.keys(totalInstallations).reduce((total, platformName) => total + totalInstallations[platformName], 0) }</span></div>
							}
						</CardContent>
					</Card>
				</div>
			);
		}

		return (
			<div>
				<Typography style={{margin: '2em 0 0 2em'}}>Aplicação:</Typography>
				<Select
					label="Aplicação"
					value={application}
					onChange={this.handleSelectApplication}
					style={{margin: '2em 0 0 2em'}}
				>
					<MenuItem value="3ftlLhJYXpVqmP5joDcbVW" >OAB de Bolso</MenuItem>
					<MenuItem value="40yXWykpgaVGXsnXrT8nCc" >Concursos de Bolso</MenuItem>
				</Select>
				<br />
				<br />
				{ content }
			</div>
		)
	}
}



// WEBPACK FOOTER //
// ./src/containers/InstallationsReport.js